import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import LoginPage from "@/pages/LoginPage";
import AdminRegisterPage from "@/pages/AdminRegisterPage";
import AdminDashboard from "@/pages/AdminDashboard";
import Dashboard from "@/pages/Dashboard";
import MeetingList from "@/pages/MeetingList";
import NewMeeting from "@/pages/NewMeeting";
import AttendanceCheckInPage from "@/pages/AttendanceCheckInPage";
import NotFound from "@/pages/NotFound";
import Index from "@/pages/Index";
import MembersAttendancePage from "@/pages/MembersAttendancePage";
import DatabaseTest from '@/pages/DatabaseTest';

const queryClient = new QueryClient();

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }: { 
  children: React.ReactNode,
  requireAdmin?: boolean 
}) => {
  const { user, loading } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/" replace />;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex h-screen overflow-hidden"
    >
      <Sidebar 
        activePath={window.location.pathname} 
        isMobileMenuOpen={isMobileMenuOpen}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onMobileMenuClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
        <main className="flex-1 overflow-auto bg-gray-50">
          {children}
        </main>
      </div>
    </motion.div>
  );
};

// Admin Layout Component
const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <ProtectedRoute requireAdmin={true}>
      {children}
    </ProtectedRoute>
  );
};

// Public Route Component
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    if (user.is_admin) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const AppRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes */}
        <Route 
          path="/" 
          element={
            <PublicRoute>
              <Index />
            </PublicRoute>
          } 
        />
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/admin/register" 
          element={
            <PublicRoute>
              <AdminRegisterPage />
            </PublicRoute>
          } 
        />
        
        {/* Public Attendance Routes */}
        <Route path="/app/attend/:linkToken" element={<AttendanceCheckInPage />} />
        <Route path="/members/:adminId" element={<MembersAttendancePage />} />
        
        {/* User Dashboard Route */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          } 
        />
        <Route 
          path="/admin/meetings" 
          element={
            <AdminLayout>
              <MeetingList />
            </AdminLayout>
          } 
        />
        <Route 
          path="/admin/meetings/new" 
          element={
            <AdminLayout>
              <NewMeeting />
            </AdminLayout>
          } 
        />
        
        {/* Database Test Route */}
        <Route path="/db-test" element={<DatabaseTest />} />
        
        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
